import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { cn } from '@/lib/utils'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconArrowUpOutline } from '@icons/IconArrowUpOutline'
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { FieldRenderer } from '../../FormComponent/FieldRenderer'
import { FormValues, JsonFormSchemaV2 } from '../../FormComponent/types'
import { cleanupFormData, createDefaultValues, generateZodSchema } from '../../FormComponent/utils'
import { isFieldVisible } from '../../FormComponent/fieldVisibility'
import { useConditionalRuleFields } from '../../FormComponent/useConditionalRuleFields'

// Define a type for the imperative handle
export type FormContentHandle = {
  reset: () => void
}

interface TileFilterContentProps {
  schema: JsonFormSchemaV2
  submitHandler: (data: FormValues) => void
  resetDefaults?: FormValues
  className?: string
  isSubmitting?: boolean
}

export const TileFilterContent = forwardRef<FormContentHandle, TileFilterContentProps>(function TileFilterContent(
  { schema, submitHandler, resetDefaults, className, isSubmitting },
  ref
) {
  const { properties, required } = schema
  const zodSchema = generateZodSchema(properties, required || [])

  const defaultValues = useMemo(() => createDefaultValues(properties), [properties])

  const form = useForm<FormValues>({
    resolver: zodResolver(zodSchema),
    defaultValues,
  })
  const visibleWhenMatchedIds = useConditionalRuleFields(properties, 'visibleWhen', form.control)

  const { reset } = form

  // Expose the reset method via useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      reset: () => reset(defaultValues),
    }),
    [reset, defaultValues]
  )

  useEffect(() => {
    if (resetDefaults) {
      reset(resetDefaults)
    }
  }, [resetDefaults, reset])

  const onSubmit = (data: FormValues) => {
    const cleanedData = cleanupFormData(data)
    form.reset(data)
    submitHandler(cleanedData as FormValues)
  }

  const submitButton = isSubmitting ? <LoadingButton /> : <ApplyButton isDirty={form.formState.isDirty} />

  const visibleProperties = useMemo(
    () => properties.filter((property) => isFieldVisible(property, {}, visibleWhenMatchedIds)),
    [properties, visibleWhenMatchedIds]
  )

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn(
          'flex flex-col items-stretch [&>*:not(:first-child)]:pt-2 [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:pb-2',
          className
        )}
      >
        {visibleProperties.map((property) => (
          <FieldRenderer field={property} control={form.control} variant="column" key={property.id} />
        ))}
        {submitButton}
      </form>
    </Form>
  )
})

export function LoadingButton() {
  return (
    <div className="flex w-full justify-center">
      <SpinnerIosRegular fontSize={20} className="animate-spin" />
    </div>
  )
}

export function ApplyButton({ isDirty }: { isDirty: boolean }) {
  return (
    <div className="relative flex items-center justify-center p-1">
      <Button disabled={!isDirty} className="h-6 flex-1 text-xs" type="submit">
        <IconArrowUpOutline size={16} />
        <span className="ml-2">Apply</span>
      </Button>
      {isDirty && (
        <div className="pointer-events-none absolute inset-[22%] z-50 size-[56%] animate-ping rounded border border-primary"></div>
      )}
    </div>
  )
}
